import React from 'react';
import { BROKER_BRAND, resolveBrokerKey } from './brokerBranding';
import { brokerAccountDisplayName } from './clientReportingUtils';
import { formatLastSynced, formatThroughDate } from './clientReportingFreshness';

/**
 * Says which portfolio the dashboard is currently showing.
 *
 * Until now the only clue was the account picker in the top-right corner, which
 * is easy to miss and easy to misread — the numbers below it change completely
 * depending on that selection, with nothing near them saying what they belong
 * to. This puts the name where the data is.
 */

function BrokerBadge({ client }) {
  const key = resolveBrokerKey(client?.name, client?.broker);
  const brand = key ? BROKER_BRAND[key] : null;
  if (brand?.logoSrc) {
    return (
      <span className={`vd-broker-logo ${brand.tone} has-image`} aria-hidden="true">
        <img src={brand.logoSrc} alt="" />
      </span>
    );
  }
  return (
    <span className={`vd-broker-logo ${brand?.tone || 'neutral'}`} aria-hidden="true">
      {(client?.broker || client?.name || '?').slice(0, 1).toUpperCase()}
    </span>
  );
}

export default function ClientReportingDashboardHeader({
  clients = [],
  chartData = [],
  health = null,
}) {
  const single = clients.length === 1 ? clients[0] : null;

  const title = single
    ? brokerAccountDisplayName(single)
    : `${clients.length || 'All'} portfolios`;

  // The newest point actually being charted. Deliberately NOT "now": it answers
  // "how current is what I am looking at", which is the question a stale
  // account raises and a clock cannot answer.
  const lastPoint = chartData.length ? chartData[chartData.length - 1] : null;
  const through = formatThroughDate(lastPoint?.date || lastPoint?.rawDate || null);

  // Shown when any linked broker records a successful sync watermark.
  const syncedAt = health?.lastSyncedAt || null;

  const meta = [];
  if (single?.broker) meta.push(single.broker);
  else if (clients.length > 1) meta.push('Combined view');
  if (through) meta.push(`Data through ${through}`);
  if (syncedAt) meta.push(`Synced ${formatLastSynced(syncedAt)}`);

  return (
    <div className="vd-cr-view-header">
      {single ? <BrokerBadge client={single} /> : null}
      <div className="vd-cr-view-heading">
        <h2 className="vd-cr-view-title">{title}</h2>
        {meta.length ? (
          <p className="vd-cr-view-meta">
            {meta.map((part, i) => (
              <React.Fragment key={part}>
                {i > 0 ? <span className="vd-cr-view-sep" aria-hidden="true">·</span> : null}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </p>
        ) : null}
      </div>
    </div>
  );
}
